import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  toCsv,
  type CustomerImportRequest,
  type CustomerImportResult,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { importCustomers, previewCustomerImport } from './import-api.js';

/**
 * Importing, from the browser's side.
 *
 * Two mutations rather than one with a flag, because they are two different commitments: the
 * preview can be run again and again while the owner corrects the mapping, and the import
 * happens once. A successful import invalidates the customer list and the onboarding
 * checklist, both of which it has just changed.
 */

export function usePreviewImport(): UseMutationResult<
  CustomerImportResult,
  unknown,
  CustomerImportRequest
> {
  const client = useApiClient();
  return useMutation({
    mutationFn: (request: CustomerImportRequest) => previewCustomerImport(client, request),
  });
}

export function useImportCustomers(): UseMutationResult<
  CustomerImportResult,
  unknown,
  CustomerImportRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CustomerImportRequest) => importCustomers(client, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/**
 * The rejected rows, as a file the owner can fix and upload again.
 *
 * Written with the ORIGINAL cells and the original header, plus one column saying what was
 * wrong — so the file that comes back is the file they know, with the errors in it, rather
 * than a report they would have to transcribe. Rows that imported are left out: re-uploading
 * the corrected file must not create everybody twice.
 */
export function rejectedRowsCsv(
  header: readonly string[],
  result: CustomerImportResult,
  reasonHeading: string,
): string | null {
  const rejected = result.rows.filter((row) => row.status === 'failed' || row.status === 'skipped');
  if (rejected.length === 0) return null;

  return toCsv([
    [...header, reasonHeading],
    ...rejected.map((row) => [...row.cells, row.issues.map((issue) => issue.message).join('; ')]),
  ]);
}
